import { DEFAULT_EMAIL_BODY_TEMPLATE, EMAIL_BODY_CONTENT_TOKEN, resolveEmailBodyTemplate } from "./notify-email-body-template";
import { getSiteBrandName } from "@/lib/site/brand";
import type { EmailBrandingSettings, EmailNotifyTemplate } from "@/lib/settings/basic-page";

export function getEmailTemplateId(template: Pick<EmailNotifyTemplate, "key">) {
  let a = 2166136261, b = 5381;
  for (let index = 0; index < template.key.length; index++) {
    const code = template.key.charCodeAt(index);
    a = Math.imul(a ^ code, 16777619) >>> 0;
    b = (Math.imul(b, 33) ^ code) >>> 0;
  }
  return `EM${a.toString(16).padStart(8, "0")}${b.toString(16).padStart(8, "0")}`.toUpperCase();
}
export function isVerificationEmailTemplate(template: EmailNotifyTemplate) {
  return template.kind ? template.kind === "verification" : template.key === "email_verify" || template.body.includes("{code}");
}
export function getEmailTemplateExpireMinutes(template?: EmailNotifyTemplate) {
  return template?.expireMinutes ?? 5;
}
export function findEmailVerificationTemplate(templates: EmailNotifyTemplate[], id?: string) {
  return templates.find((item) => id ? getEmailTemplateId(item) === id : item.key === "email_verify");
}
export function normalizeEmailBranding(value: unknown): EmailBrandingSettings {
  const raw = value && typeof value === "object" ? value as Record<string, unknown> : {};
  return { senderName: typeof raw.senderName === "string" ? raw.senderName.trim() : "" };
}
export function validateEmailBranding(value: EmailBrandingSettings) {
  if (value.senderName.length > 100 || /[\r\n]/.test(value.senderName)) return "发信名称最多100字且不能换行";
  return null;
}

export function normalizeEmailSenderTokens(text: string) {
  return text.replace(/【品牌名】|\{brand\}/g, token => token === "【品牌名】" ? "【发信名称】" : "{senderName}");
}
export function normalizeEmailTemplateBody(item: Record<string, unknown>, fallback = "") {
  return normalizeEmailSenderTokens(typeof item.body === "string" ? item.body : typeof item.subject === "string" && item.subject.trim() ? item.subject : fallback);
}
export function validateEmailTemplateContent(template: EmailNotifyTemplate): string | null {
  if (template.body.length > 50_000) return "邮件正文不能超过50000字";
  if (!Number.isInteger(getEmailTemplateExpireMinutes(template)) || getEmailTemplateExpireMinutes(template) < 1 || getEmailTemplateExpireMinutes(template) > 1440) return "有效期须为1～1440分钟的整数";
  if (!template.enabled) return null;
  if (!template.subject.trim()) return "启用的邮件模板需要填写邮件标题";
  if (!template.body.trim()) return "启用的邮件模板需要填写邮件正文";
  if (isVerificationEmailTemplate(template) && !template.body.includes("{code}")) return "邮箱验证正文必须包含 {code} 验证码变量";
  return null;
}
export type NotifyTemplateVars = { code: string; brand: string; expireMinutes?: number };
export type EmailNotifyTemplateVars = Omit<NotifyTemplateVars, "brand"> & { senderName: string };
export function renderNotifyTemplate(template: string, vars: EmailNotifyTemplateVars) {
  return template.replace(/\{code\}|【发信名称】|\{senderName\}|\{time\}/g, (token) => {
    if (token === "{code}") return vars.code;
    if (token === "【发信名称】") return `【${vars.senderName}】`;
    if (token === "{senderName}") return vars.senderName;
    return vars.expireMinutes === undefined ? token : String(vars.expireMinutes);
  });
}
export function renderEmailNotifyContent(template: { subject: string; body?: string }, vars: EmailNotifyTemplateVars) {
  return { subject: renderNotifyTemplate(template.subject, vars), text: renderNotifyTemplate(template.body ?? template.subject, vars) };
}

function escapeEmailHtml(value: string) {
  return value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]!);
}
export type EmailBrandStyle = { senderName: string; brandName: string };
export function resolveEmailBrandStyle(branding: EmailBrandingSettings | undefined, site: { siteShortName: string; siteLogo?: string; siteUrl?: string }): EmailBrandStyle {
  const brandName = getSiteBrandName(site.siteShortName);
  const name = branding?.senderName.trim() || brandName;
  return { senderName: name, brandName };
}

export function renderBrandedEmail(content: { subject: string; text: string }, bodyTemplate = DEFAULT_EMAIL_BODY_TEMPLATE) {

  const html = resolveEmailBodyTemplate(bodyTemplate).replace(/\{content\}|\{subject\}/g, token => token === EMAIL_BODY_CONTENT_TOKEN
    ? escapeEmailHtml(content.text).replace(/\r?\n/g, "<br>")
    : escapeEmailHtml(content.subject));
  return { ...content, html };
}
